import React, { useEffect, useState } from 'react';
import { useData } from '../../../hooks/useData';
import { useToast } from '../../../hooks/useToast';
import { Tag } from '../../common/Tag';
import { usersManagementServices } from '../../../APIServices/usersManagementServices';
import Pagination from '../../../hooks/Pagination';
import AddNewUser from './AddNewUser';
import UpdateUser from './UpdateUser';
import DeletedUserHistory from './DeletedUserHistory';
import DeleteUser from './DeleteUser';

function UserManagement() {
  const toast = useToast();
  const [userList, setUserList] = useState([]);
  const [userCount, setUserCount] = useState(0);

  const [deleteUserList, setDeleteUserList] = useState([]);
  const [deleteUserCount, setDeleteUserCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState("");
  const [confirm, setConfirm] = useState(false);

  const [user, setUser] = useState("");
  const [userData, setUserData] = useState("");

  const [searchData, setSearchData] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const startIndex = (pageNumber - 1) * itemsPerPage;
  const totalPage = Math.ceil(userCount / itemsPerPage);

  const getUserData = async (e) => {
    setLoading(true);
    let data = await usersManagementServices.getUserData(
      "all",
      "user",
      searchData,
      pageNumber,
      itemsPerPage
    );
    setUserList(data?.data || []);
    setUserCount(data?.count || 0);
    setLoading(false);
  };

  const [deletedPageNumber, setDeletedPageNumber] = useState(1);
  const [deletedItemsPerPage, setDeletedItemsPerPage] = useState(10);

  const getDeletedUserData = async (deletedPageNumber, deletedItemsPerPage) => {
    setLoading(true);
    let data = await usersManagementServices.getUserData(
      "deleted",
      "user",
      "",
      deletedPageNumber,
      deletedItemsPerPage
    );
    setDeleteUserList(data?.data || []);
    setDeleteUserCount(data?.count || 0);
    setLoading(false);
  };

  useEffect(() => {
    getUserData(pageNumber, itemsPerPage);
  }, [pageNumber, itemsPerPage]);

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-eyebrow">
            User administration
          </div>

          <h1 className="page-title">
            User Management
          </h1>

          <p className="page-sub">
            Create, manage, and monitor user accounts across the platform. Assign
            roles, designations, and permissions to ensure secure and efficient access
            to system features and modules.
          </p>
        </div>
      </div>

      <div className="flex between center mb-3">
        <div className="section-label">
          Users{' '}
          <span className="count">
            {userCount}
          </span>
        </div>

        <div className='hstack gap-4'>
          <button
            className="btn btn-primary"
            onClick={() => setModalOpen("add")}
          >
            <i className="ti ti-plus" />
            Add User
          </button>
          <button
            className="btn btn-danger"
            onClick={() => setModalOpen("deletedHistiory")}
          >
            <i className="ti ti-history" />
            Deleted History
          </button>
        </div>
      </div>
      <div className="card">
        {loading ? (
            <div
              style={{
                padding: 40,
                textAlign: "center",
              }}
            >
              <div className="buttonLoader" />

              <div
                style={{
                  marginTop: 12,
                }}
              >
                Loading Users...
              </div>
            </div>
          ) : userCount === 0 ? (
          <div
            style={{
              padding: 24,
              textAlign: 'center',
              color: 'var(--ink-3)',
            }}
          >
            <i
              className="ti ti-users"
              style={{
                fontSize: 24,
                display: 'block',
                marginBottom: 8,
              }}
            />

            No users defined. Click "Add User" to create one.
          </div>
        ) : (
          <>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Full Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Gender</th>
                  <th>Designation</th>
                  <th>Status</th>
                  <th style={{ width: 120 }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {userList.map((item) => (
                  <tr key={item.id}>
                    <td className="medium">
                      {[item.firstName, item.middleName, item.lastName]
                        .filter(Boolean)
                        .join(" ")}
                    </td>
                    <td>{item?.email}</td>
                    <td>{item?.phone || "_"}</td>
                    <td>{item?.gender === "male" ? "Male" : item?.gender === "female" ? "Female" : item?.gender === "other" ? "Other" : "_"}</td>
                    <td>{item?.designation || "_"}</td>
                    <td className='text-capitalize'>
                      <Tag variant={item?.status === "active" ? "ok" : item?.status === "inactive" ? "danger" : item?.status === "freeze" ? "warn" : "default"}>
                        {item?.status}
                      </Tag>
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button
                          className="icon-btn"
                          title="Edit"
                          style={{
                            color:
                              'var(--info)',
                          }}
                          onClick={() => { setModalOpen("update"); setUserData(item); setUser(item?.user) }}
                        >
                          <i className="ti ti-edit" />
                        </button>
                        <button
                          className="icon-btn"
                          title="Delete"
                          style={{
                            color:
                              'var(--danger)',
                          }}
                          onClick={() => { setModalOpen("delete"); setUserData(item); }}
                        >
                          <i className="ti ti-trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
              <Pagination
                itemsPerPage={itemsPerPage}
                setItemsPerPage={setItemsPerPage}
                pageNumber={pageNumber}
                setPageNumber={setPageNumber}
                totalPage={totalPage}
                totalItems={userCount}
              />
          </>
        )}
      </div>

      {modalOpen === "add" && (
        <AddNewUser
          setModalOpen={setModalOpen}
          getUserData={getUserData}
          title="User"
          roleType="user"
        />
      )}
      {modalOpen === "update" && (
        <UpdateUser
          userData={userData}
          setModalOpen={setModalOpen}
          getUserData={getUserData}
          setConfirm={setConfirm}
          confirm={confirm}
          title="User"
        />
      )}
      {modalOpen === "delete" && (
        <DeleteUser
          getUserData={getUserData}
          getDeletedUserData={getDeletedUserData}
          setModalOpen={setModalOpen}
          userData={userData}
          title="User"
        />
      )}
      {modalOpen === "deletedHistiory" && (
        <DeletedUserHistory
          getUserData={getUserData}
          getDeletedUserData={getDeletedUserData}
          setModalOpen={setModalOpen}
          deletedPageNumber={deletedPageNumber}
          setDeletedPageNumber={setDeletedPageNumber}
          deletedItemsPerPage={deletedItemsPerPage}
          setDeletedItemsPerPage={setDeletedItemsPerPage}
          setDeleteUserList={setDeleteUserList}
          deleteUserList={deleteUserList}
          setDeleteUserCount={setDeleteUserCount}
          deleteUserCount={deleteUserCount}
          title="User"
        />
      )}
    </div>
  );
}

export default UserManagement;